import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SearchBar from "@/components/feature/SearchBar";
import MovieCard from "@/components/feature/MovieCard";
import SafeImage from "@/components/base/SafeImage";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { allCategories, hotMovies, searchMovies, searchNews, shortDate } from "@/lib/catalog";

const hotKeywords = ["雨后", "新宿", "胶片", "佐野川透", "京都", "影评", "修复", "黑色电影"];

export default function SearchPage() {
  const [params] = useSearchParams();
  const keyword = (params.get("q") || "").trim();

  const movies = useMemo(() => searchMovies(keyword), [keyword]);
  const news = useMemo(() => searchNews(keyword), [keyword]);
  const total = movies.length + news.length;

  useDocumentMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 日本情色电影` : "搜索 - 日本情色电影",
    description: keyword
      ? `在日本情色电影的影片与资讯中搜索“${keyword}”，共找到 ${total} 条结果，包含影片条目与相关文章。`
      : "搜索日本情色电影的全部影片与资讯：可按片名、导演、演员、地区或文章标题查找。",
    keywords: keyword ? `${keyword},站内搜索,日本情色电影` : "站内搜索,日本情色电影,影片检索",
  });

  const hasQuery = keyword.length > 0;

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full border-b border-background-300/50 bg-background-100/50">
          <div className="mx-auto w-full max-w-[1000px] px-4 py-10 md:px-8 md:py-14">
            <p className="text-[11px] tracking-widest2 text-accent-500 font-label">SEARCH</p>
            <h1 className="mt-2 font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
              {hasQuery ? (
                <>
                  搜索：<span className="text-accent-400">“{keyword}”</span>
                </>
              ) : (
                "搜索影片与资讯"
              )}
            </h1>
            <p className="mt-2 text-[12px] leading-relaxed text-foreground-500">
              支持片名（中文 / 日文）、导演、演员、地区、标签，以及资讯标题与正文关键词。
            </p>

            <div className="mt-5">
              <SearchBar key={keyword} defaultKeyword={keyword} size="lg" />
            </div>

            {hasQuery && (
              <p className="mt-4 flex flex-wrap items-center gap-2 text-[12px] text-foreground-400 font-label">
                <i className="ri-list-check text-accent-500" />
                共找到 <span className="font-semibold text-foreground-100">{total}</span> 条结果 · 影片{" "}
                <span className="text-foreground-100">{movies.length}</span> 部 · 资讯{" "}
                <span className="text-foreground-100">{news.length}</span> 篇
              </p>
            )}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8 md:py-12">
          {!hasQuery && (
            <div className="mx-auto max-w-[1000px]">
              <div className="rounded-lg border border-dashed border-background-400/60 bg-background-100/40 px-6 py-12 text-center">
                <i className="ri-search-line text-3xl text-foreground-600" />
                <h2 className="mt-3 font-heading text-base font-semibold text-foreground-100">
                  输入关键词开始查找
                </h2>
                <p className="mt-2 text-[12px] text-foreground-500">
                  试试下面的热门关键词，或直接从分类入口浏览。
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {hotKeywords.map((word) => (
                    <Link
                      key={word}
                      to={`/search?q=${encodeURIComponent(word)}`}
                      className="cursor-pointer rounded-full border border-background-300/70 px-3 py-1.5 text-[12px] text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400 font-label"
                    >
                      {word}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-10">
                <h2 className="font-heading text-base font-semibold text-foreground-100">热门分类</h2>
                <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
                  {allCategories.map((category) => (
                    <Link
                      key={category.slug}
                      to={`/category/${category.slug}`}
                      className="group flex cursor-pointer items-center gap-3 rounded-lg border border-background-300/60 bg-background-100 px-4 py-3 transition-colors hover:border-accent-500/60"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-primary-700/60 bg-primary-900/30 text-accent-400">
                        <i className={`${category.icon} text-sm`} />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-heading text-[13px] text-foreground-100 group-hover:text-accent-400">
                          {category.name}
                        </span>
                        <span className="block truncate text-[10px] text-foreground-600 font-label">{category.en}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {hasQuery && total === 0 && (
            <div className="mx-auto max-w-[1000px]">
              <div className="rounded-lg border border-dashed border-background-400/60 bg-background-100/40 px-6 py-14 text-center">
                <i className="ri-emotion-sad-line text-3xl text-foreground-600" />
                <h2 className="mt-3 font-heading text-base font-semibold text-foreground-100">
                  没有找到与“{keyword}”相关的内容
                </h2>
                <p className="mt-2 text-[12px] leading-relaxed text-foreground-500">
                  可以换一个更短的关键词，或试试导演、演员、地区名。也可以从热门分类进入。
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {allCategories.slice(0, 4).map((category) => (
                    <Link
                      key={category.slug}
                      to={`/category/${category.slug}`}
                      className="cursor-pointer rounded-full border border-background-300/70 px-3 py-1.5 text-[12px] text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400 font-label"
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-10">
                <h2 className="font-heading text-base font-semibold text-foreground-100">也许你会想看</h2>
                <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4">
                  {hotMovies(4).map((movie) => (
                    <MovieCard key={movie.id} movie={movie} showSynopsis={false} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {hasQuery && movies.length > 0 && (
            <div>
              <div className="flex flex-wrap items-end justify-between gap-3 border-b border-background-300/50 pb-4">
                <h2 className="font-heading text-lg font-bold text-foreground-50">
                  影片结果
                  <span className="ml-2 font-display text-sm font-normal text-foreground-500">({movies.length})</span>
                </h2>
                <Link
                  to="/category/new"
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-[11px] text-accent-400 transition-colors hover:text-accent-300 font-label"
                >
                  按分类浏览
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4 xl:grid-cols-6">
                {movies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          )}

          {hasQuery && news.length > 0 && (
            <div className={movies.length > 0 ? "mt-12" : ""}>
              <div className="flex flex-wrap items-end justify-between gap-3 border-b border-background-300/50 pb-4">
                <h2 className="font-heading text-lg font-bold text-foreground-50">
                  资讯结果
                  <span className="ml-2 font-display text-sm font-normal text-foreground-500">({news.length})</span>
                </h2>
                <Link
                  to="/news"
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-[11px] text-accent-400 transition-colors hover:text-accent-300 font-label"
                >
                  全部资讯
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>

              <ul className="mt-2 flex flex-col divide-y divide-background-300/50">
                {news.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/news/detail/${item.id}`}
                      className="group flex cursor-pointer flex-col gap-4 py-5 sm:flex-row sm:items-center sm:gap-6"
                    >
                      <div className="h-[120px] w-full shrink-0 overflow-hidden rounded-lg border border-background-300/50 bg-background-200 sm:h-[92px] sm:w-[150px]">
                        <SafeImage
                          src={item.cover}
                          alt={`${item.title} 配图`}
                          title={`${item.title} - 日本情色电影资讯`}
                          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] tracking-widest2 text-foreground-500 font-label">
                          <span className="text-accent-500">{item.category}</span>
                          <span>{shortDate(item.date)}</span>
                        </div>
                        <h3 className="mt-1.5 font-heading text-[14px] font-semibold leading-snug text-foreground-100 transition-colors group-hover:text-accent-400">
                          {item.title}
                        </h3>
                        <p className="mt-1.5 line-clamp-1 text-[12px] text-foreground-500">{item.excerpt}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}